import { PaginationDropdownLimit } from '@mesa-labs/mesa-ui/dist/components/Pagination';
import { types as api } from '@mesa-labs/mesa-api';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AmountFilterState = {
  operator: 'gt' | 'lt' | 'eq' | 'range';
  value: number;
  valueTo?: number;
} | undefined;

export type SelectedInvoiceData = {
  id: string;
  invoiceNumber: string;
  externalClientId: string;
  clientName: string;
  invoiceDate?: string;
  dueDate?: string;
  amount: number;
  externalInvoiceIdentifier?: string;
  invoiceId?: string;
};

export type TransactionMatchingState = {
  // Transaction list filters
  filterTab?: 'matches' | 'unmatched' | 'all';
  transactionSearchQuery?: string;
  transactionDateFrom?: string;
  transactionDateTo?: string;
  transactionAmountMin?: number;
  transactionLabelFilter?: string;
  transactionBankAccountFilter?: string;
  // Transaction selection
  selectedTransactionId?: string;
  selectedTransactionAccountId?: string;
  // Suggestion params
  suggestionParams?: api.ReconcileMatchingParams;
  // Invoice table state
  invoiceSubTab?: 'suggested' | 'unmatched' | 'open';
  selectedInvoiceIds?: string[];
  selectedMatchedInvoiceIds?: string[];
  invoiceSearchQuery?: string;
  invoiceClientFilter?: string;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  invoiceAmountFilter?: AmountFilterState;
  invoicesPage?: number;
  invoicesTotalPages?: number;
  invoicesLimit?: PaginationDropdownLimit;
  invoicesSortField?: string;
  invoicesSortDirection: api.SortDirection;
  // Selected invoice data for running totals and confirmation
  selectedInvoiceData: Record<string, SelectedInvoiceData>;
};

export const initialState: TransactionMatchingState = {
  filterTab: 'matches',
  transactionSearchQuery: undefined,
  transactionDateFrom: undefined,
  transactionDateTo: undefined,
  transactionAmountMin: undefined,
  transactionLabelFilter: undefined,
  transactionBankAccountFilter: undefined,
  selectedTransactionId: undefined,
  selectedTransactionAccountId: undefined,
  suggestionParams: undefined,
  invoiceSubTab: 'open',
  selectedInvoiceIds: [],
  selectedMatchedInvoiceIds: [],
  invoiceSearchQuery: undefined,
  invoiceClientFilter: undefined,
  invoiceDateFrom: undefined,
  invoiceDateTo: undefined,
  invoiceAmountFilter: undefined,
  invoicesPage: 1,
  invoicesTotalPages: undefined,
  invoicesLimit: { key: '10', value: 10 },
  invoicesSortField: 'invoiceDate',
  invoicesSortDirection: api.SortDirection.DESCENDING,
  selectedInvoiceData: {},
};

const transactionMatchingSlice = createSlice({
  name: 'transactionMatching',
  initialState,
  reducers: {
    // Transaction list filters
    setFilterTab(state: TransactionMatchingState, action: PayloadAction<'matches' | 'unmatched' | 'all'>) {
      state.filterTab = action.payload;
    },
    setTransactionSearchQuery(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.transactionSearchQuery = action.payload;
    },
    setTransactionDateFrom(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.transactionDateFrom = action.payload;
    },
    setTransactionDateTo(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.transactionDateTo = action.payload;
    },
    setTransactionAmountMin(state: TransactionMatchingState, action: PayloadAction<number | undefined>) {
      state.transactionAmountMin = action.payload;
    },
    setTransactionLabelFilter(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.transactionLabelFilter = action.payload;
    },
    setTransactionBankAccountFilter(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.transactionBankAccountFilter = action.payload;
    },
    // Suggestion params
    setSuggestionParams(state: TransactionMatchingState, action: PayloadAction<api.ReconcileMatchingParams | undefined>) {
      state.suggestionParams = action.payload;
    },
    // Transaction selection
    selectTransaction(state: TransactionMatchingState, action: PayloadAction<{ transactionId: string; vendorAccountId: string } | null>) {
      if (action.payload) {
        state.selectedTransactionId = action.payload.transactionId;
        state.selectedTransactionAccountId = action.payload.vendorAccountId;
      } else {
        state.selectedTransactionId = undefined;
        state.selectedTransactionAccountId = undefined;
      }
      state.selectedInvoiceIds = [];
      state.selectedInvoiceData = {};
      state.selectedMatchedInvoiceIds = [];
      state.invoiceSubTab = action.payload ? 'suggested' : 'open';
      state.invoicesPage = 1;
    },
    // Invoice table state
    setInvoiceSubTab(state: TransactionMatchingState, action: PayloadAction<'suggested' | 'unmatched' | 'open'>) {
      state.invoiceSubTab = action.payload;
      state.invoicesPage = 1;
    },
    toggleInvoiceSelection(state: TransactionMatchingState, action: PayloadAction<string>) {
      const ids = state.selectedInvoiceIds || [];
      const index = ids.indexOf(action.payload);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        ids.push(action.payload);
      }
      state.selectedInvoiceIds = ids;
    },
    setSelectedInvoiceIds(state: TransactionMatchingState, action: PayloadAction<string[]>) {
      state.selectedInvoiceIds = action.payload;
    },
    setSelectedInvoiceData(state: TransactionMatchingState, action: PayloadAction<Record<string, SelectedInvoiceData>>) {
      state.selectedInvoiceData = action.payload;
    },
    clearInvoiceSelections(state: TransactionMatchingState) {
      state.selectedInvoiceIds = [];
      state.selectedInvoiceData = {};
    },
    toggleMatchedInvoiceSelection(state: TransactionMatchingState, action: PayloadAction<string>) {
      const ids = state.selectedMatchedInvoiceIds || [];
      const index = ids.indexOf(action.payload);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        ids.push(action.payload);
      }
      state.selectedMatchedInvoiceIds = ids;
    },
    setSelectedMatchedInvoiceIds(state: TransactionMatchingState, action: PayloadAction<string[]>) {
      state.selectedMatchedInvoiceIds = action.payload;
    },
    clearMatchedInvoiceSelections(state: TransactionMatchingState) {
      state.selectedMatchedInvoiceIds = [];
    },
    setInvoiceSearchQuery(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.invoiceSearchQuery = action.payload;
      state.invoicesPage = 1;
    },
    setInvoiceClientFilter(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.invoiceClientFilter = action.payload;
      state.invoicesPage = 1;
    },
    setInvoiceDateFrom(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.invoiceDateFrom = action.payload;
      state.invoicesPage = 1;
    },
    setInvoiceDateTo(state: TransactionMatchingState, action: PayloadAction<string | undefined>) {
      state.invoiceDateTo = action.payload;
      state.invoicesPage = 1;
    },
    setInvoiceAmountFilter(state: TransactionMatchingState, action: PayloadAction<AmountFilterState>) {
      state.invoiceAmountFilter = action.payload;
      state.invoicesPage = 1;
    },
    updateInvoicesPage(state: TransactionMatchingState, action: PayloadAction<number>) {
      state.invoicesPage = action.payload;
    },
    updateInvoicesTotalPages(state: TransactionMatchingState, action: PayloadAction<number>) {
      state.invoicesTotalPages = action.payload;
    },
    updateInvoicesLimit(state: TransactionMatchingState, action: PayloadAction<PaginationDropdownLimit>) {
      state.invoicesLimit = action.payload;
      state.invoicesPage = 1;
    },
    updateInvoicesSortField(state: TransactionMatchingState, action: PayloadAction<string>) {
      state.invoicesSortField = action.payload;
    },
    updateInvoicesSortDirection(state: TransactionMatchingState, action: PayloadAction<api.SortDirection>) {
      state.invoicesSortDirection = action.payload;
    },
  },
});

export const {
  setFilterTab,
  setSuggestionParams,
  setTransactionSearchQuery,
  setTransactionDateFrom,
  setTransactionDateTo,
  setTransactionAmountMin,
  setTransactionLabelFilter,
  setTransactionBankAccountFilter,
  selectTransaction,
  setInvoiceSubTab,
  toggleInvoiceSelection,
  setSelectedInvoiceIds,
  setSelectedInvoiceData,
  clearInvoiceSelections,
  toggleMatchedInvoiceSelection,
  setSelectedMatchedInvoiceIds,
  clearMatchedInvoiceSelections,
  setInvoiceSearchQuery,
  setInvoiceClientFilter,
  setInvoiceDateFrom,
  setInvoiceDateTo,
  setInvoiceAmountFilter,
  updateInvoicesPage,
  updateInvoicesTotalPages,
  updateInvoicesLimit,
  updateInvoicesSortField,
  updateInvoicesSortDirection,
} = transactionMatchingSlice.actions;
export default transactionMatchingSlice.reducer;
